import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CLARIFICATION_MODE_OPTIONS,
  CLARIFICATION_TIMEOUT_OPTIONS,
  useClarificationPrefsStore,
  type ClarificationMode,
} from '@/stores/clarification-prefs-store';

export interface ClarificationModeFieldsProps {
  /** Prefixes every field id, so two instances never collide if both mount. */
  idPrefix: string;
}

/**
 * The radio group of clarification modes, with the timeout dropdown nested
 * under "Ask with timeout". Shared by the chat settings popover and the
 * workflow footer pill -- the two read/write the same
 * useClarificationPrefsStore state and previously kept separate copies of
 * this markup that drifted in wording.
 */
export function ClarificationModeFields({
  idPrefix,
}: ClarificationModeFieldsProps) {
  const { setMode, setTimeoutSecs } = useClarificationPrefsStore();
  const mode = useClarificationPrefsStore(
    (s) => s.workflowSession?.mode ?? s.mode,
  );
  const timeoutSecs = useClarificationPrefsStore(
    (s) => s.workflowSession?.timeoutSecs ?? s.timeoutSecs,
  );

  return (
    <RadioGroup
      value={mode}
      onValueChange={(value) => setMode(value as ClarificationMode)}
      className='gap-3'
    >
      {CLARIFICATION_MODE_OPTIONS.map((option) => {
        const id = `${idPrefix}-${option.value}`;
        return (
          <div key={option.value} className='flex items-start gap-3'>
            <RadioGroupItem value={option.value} id={id} className='mt-1' />
            <div className='flex-1'>
              <Label
                htmlFor={id}
                className='cursor-pointer text-sm font-medium'
              >
                {option.label}
              </Label>
              <p className='mt-0.5 text-xs text-muted-foreground'>
                {option.description}
              </p>
              {option.value === 'ask_with_timeout' && (
                <Select
                  value={String(timeoutSecs)}
                  onValueChange={(value) => {
                    setTimeoutSecs(Number(value));
                    // Picking a timeout implies this mode -- flip to it so
                    // the choice actually takes effect.
                    if (mode !== 'ask_with_timeout') {
                      setMode('ask_with_timeout');
                    }
                  }}
                  disabled={mode !== 'ask_with_timeout'}
                >
                  <SelectTrigger
                    id={`${idPrefix}-timeout`}
                    aria-label='Clarification timeout'
                    className='mt-2 h-8 w-32 text-sm'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLARIFICATION_TIMEOUT_OPTIONS.map((timeoutOption) => (
                      <SelectItem
                        key={timeoutOption.value}
                        value={String(timeoutOption.value)}
                      >
                        {timeoutOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        );
      })}
    </RadioGroup>
  );
}
